import { Label, Text } from "@primer/react";
import { MButton, MCard } from "../../../components/mobile";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { IClassListItem } from "../../../model/respone/classes/IClassListItem";
import { IMyRegistration } from "../../../model/respone/registration/IMyRegistration";
import { getDebtBlock, getFinancialBlock, registerBlockedTooltip } from "../classesHelpers";
import { hasScheduleConflict } from "../../../helpers/scheduleConflict";
import TkbAgenda from "./TkbAgenda";

interface IClassCardProps {
    item: IClassListItem;
    myList: IMyRegistration[];
    pendingClassIds: number[];
    confirmedClassIds: number[];
    /** Lý do khóa cổng (ngoài giờ / chưa tải xong thời gian). */
    gateReason?: string;
    /** Hiện thông tin môn ở đầu thẻ (bảng combined theo nhóm lớp & lớp song hành). */
    showSubject?: boolean;
    /** true = thẻ con trong danh sách LTH / song hành. */
    isSub?: boolean;
    /** Loại thẻ con, chỉ có khi isSub=true. */
    subType?: "lth" | "sh";
    /** parentClassId — bắt buộc khi `isSub` để đăng ký cặp. */
    parentClassId?: number;
    /** Parent có đang mở danh sách lớp thực hành (LTH) không. */
    expanded?: boolean;
    onToggleExpand?: (classId: number) => void;
    onRegister?: (classId: number) => void;
    onPickPair?: (parentClassId: number, childClassId: number) => void;
}

/**
 * 1 lớp tín chỉ → 1 thẻ (thay cho 1 row bảng desktop).
 * Dùng cho: bảng listDetail (B3), bảng combined theo nhóm lớp (B4) và thẻ con LTH/SH.
 * Toàn bộ điều kiện (full / locked / pending / Rule 12...) port nguyên từ ClassesPage desktop.
 */
const ClassCard = ({
    item,
    myList,
    pendingClassIds,
    confirmedClassIds,
    gateReason,
    showSubject,
    isSub,
    subType,
    parentClassId,
    expanded,
    onToggleExpand,
    onRegister,
    onPickPair,
}: IClassCardProps) => {
    // ----- Điều kiện trạng thái (giống hệt logic inline desktop) -----
    const isFull = item.slotsLeft <= 0;
    const isLocked = isFull || !item.canRegister || !!gateReason;
    const isPending = pendingClassIds.includes(item.classId);
    const isConfirmed = confirmedClassIds.includes(item.classId);
    const isAlreadyRegistered = myList.some(
        r => r.id_lop_tc === item.classId && r.trang_thai === 1
    );
    const isSameMonRegistered = item.reasonCode === "ALREADY_REGISTERED_SAME_MON";
    const hasLth = !!(item.listThucHanh && item.listThucHanh.length > 0);
    const hasConflict = !!(item.iD_lops_TrungLich && item.iD_lops_TrungLich.length > 0);
    const highlightLocked = !isAlreadyRegistered && (isFull || hasConflict);
    // FE gate tài chính (strict mode) — đọc trực tiếp từ store thay vì prop drill qua nhiều layer.
    // null = cho phép; string = lý do block (hiển thị thay nút Đăng ký).
    const financial = useAppSelector(s => s.financial.data);
    const finBlock = getFinancialBlock(item, financial, {
        isPracticeClass: isSub === true && subType === "lth",
    });
    // Nợ học phí vượt mức (check_tai_chinh) — cấp SV, chặn mọi lớp. Hiển thị inline thay nút (mobile
    // không có hover để xem tooltip). Panel DebtNoticeWidget ở đầu trang giải thích chi tiết.
    const debtBlock = getDebtBlock(financial);

    const subListType: "lth" = "lth";
    const subClasses = hasLth ? item.listThucHanh! : [];

    // Trùng lịch tươi cho thẻ CON (LTH/SH): nested list KHÔNG được reducer cập nhật canRegister
    // sau mỗi lần ĐK → tự so TKB với lớp đã ĐK. Chỉ áp cho isSub (thẻ top-level đã được reducer
    // xử lý, gồm ngoại lệ môn chung). Backend RegisterPair vẫn là source of truth.
    const subConflictsRegistered = !!isSub && myList.some(
        r => r.trang_thai === 1 && r.id_lop_tc !== item.classId
            && hasScheduleConflict(item.thoiKhoaBieus, r.thoiKhoaBieus)
    );
    const isSubLocked = isLocked || subConflictsRegistered;
    const subLockReason = gateReason ?? (subConflictsRegistered ? "Trùng lịch với lớp đã đăng ký." : undefined);

    // ----- Nút hành động -----
    const renderAction = () => {
        if (isAlreadyRegistered || isConfirmed) {
            return (
                <Text sx={{ color: "success.fg", fontWeight: 600, fontSize: 1 }}>
                    <i className="fas fa-check" /> Đã ĐK
                </Text>
            );
        }
        if (isPending) {
            return <MButton loading variant="primary">Đang xử lý</MButton>;
        }
        if (isSameMonRegistered) {
            return (
                <Text sx={{ color: "fg.muted", fontStyle: "italic", fontSize: 0 }}>
                    Đã ĐK lớp khác cùng môn
                </Text>
            );
        }
        // Cap nhóm tự chọn đã đủ → ẩn nút Đăng ký, hiện lý do inline (đợt học chính khoá).
        if (item.reasonCode === "NHOM_TU_CHON_FULL") {
            return (
                <Text sx={{ color: "danger.fg", fontStyle: "italic", fontSize: 0 }}>
                    {item.message ?? "Đã đủ nhóm tự chọn"}
                </Text>
            );
        }
        // Nợ học phí vượt mức (check_tai_chinh) — chặn mọi lớp, hiển thị lý do inline.
        if (debtBlock) {
            return (
                <Text sx={{ color: "danger.fg", fontStyle: "italic", fontSize: 0 }}>
                    {debtBlock}
                </Text>
            );
        }
        // FE gate tài chính — ẩn hoàn toàn nút Đăng ký / Chọn LTH/SH khi SV không có hồ sơ TC
        // hoặc không đủ số dư. Banner ở FinancialSummaryWidget đã giải thích lý do tổng quát.
        if (finBlock) {
            return (
                <Text sx={{ color: "danger.fg", fontStyle: "italic", fontSize: 0 }}>
                    {finBlock}
                </Text>
            );
        }
        // Thẻ con LTH/SH → đăng ký cặp với lớp cha.
        if (isSub) {
            return (
                <MButton
                    variant="primary"
                    disabled={isSubLocked}
                    title={isSubLocked ? registerBlockedTooltip(item, isFull, subLockReason) : undefined}
                    onClick={() =>
                        parentClassId != null && onPickPair?.(parentClassId, item.classId)
                    }
                >
                    Đăng ký
                </MButton>
            );
        }
        // Lớp cha có LTH → nút mở/đóng danh sách lớp thực hành con.
        // Dùng variant "default" (xám) + mũi tên để phân biệt rõ với nút "Đăng ký" (xanh):
        // đây là bước MỞ DANH SÁCH chọn lớp con, chưa phải hành động đăng ký.
        if (hasLth) {
            const chevron = () => (
                <i className={`fas fa-chevron-${expanded ? "up" : "down"}`} />
            );
            return (
                <MButton
                    variant="default"
                    disabled={isLocked}
                    title={isLocked ? registerBlockedTooltip(item, isFull, gateReason) : undefined}
                    trailingVisual={chevron}
                    onClick={() => onToggleExpand?.(item.classId)}
                >
                    {expanded
                        ? "Đóng"
                        : `Chọn lớp TH (${subClasses.length})`}
                </MButton>
            );
        }
        // Lớp đơn lẻ.
        return (
            <MButton
                variant="primary"
                disabled={isLocked}
                title={isLocked ? registerBlockedTooltip(item, isFull, gateReason) : undefined}
                onClick={() => onRegister?.(item.classId)}
            >
                Đăng ký
            </MButton>
        );
    };

    // Lý do khóa hiển thị rõ (mobile không có hover tooltip).
    const showReason =
        isSubLocked && !isAlreadyRegistered && !isConfirmed && !isPending && !isSameMonRegistered
        && item.reasonCode !== "NHOM_TU_CHON_FULL";

    return (
        <MCard locked={highlightLocked} registered={isAlreadyRegistered}>
            <div className="m-card-head">
                <div className="m-card-head-main">
                    {showSubject && item.ten_mon && (
                        <div className="m-card-code">
                            {item.ky_hieu ? `${item.ky_hieu} · ` : ""}
                            {item.ten_mon}
                            {item.so_tin_chi ? ` · ${item.so_tin_chi} TC` : ""}
                        </div>
                    )}
                    <p className="m-card-title">{item.ten_lop_hp}</p>
                    {item.hinh_thuc_dang_ky_text && (
                        <div className="m-badge-wrap">
                            <Label variant="accent">{item.hinh_thuc_dang_ky_text}</Label>
                        </div>
                    )}
                </div>
            </div>

            <div className="m-card-body">
                <TkbAgenda tkbs={item.thoiKhoaBieus} classLevelGv={item.giao_vien} />
                {/* Học phí — LUÔN hiện khi đợt bật tinh_hoc_phi_theo_so_du, kể cả lớp thiếu đơn giá
                    (hiện 0đ + finBlock chặn nút Đăng ký với reason "Chưa có đơn giá"). */}
                {financial?.tinh_hoc_phi_theo_so_du && (
                    <Text sx={{ display: "block", fontSize: 0, color: "fg.muted", mt: "6px" }}>
                        Số tiền: <b>{(item.so_tien ?? 0).toLocaleString("vi-VN")}đ</b>
                        {" · "}Miễn giảm: <b>{(item.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}đ</b>
                        {" · "}Phải nộp: <b>{(item.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}đ</b>
                    </Text>
                )}
            </div>

            <div className="m-card-foot">
                {isFull ? (
                    <Text sx={{ color: "danger.fg", fontWeight: 600, fontSize: 1 }}>
                        <i className="fas fa-ban" /> Hết chỗ
                    </Text>
                ) : (
                    <Text sx={{ fontSize: 1 }}>
                        Còn trống:{" "}
                        <Text sx={{ fontWeight: 700, color: "success.fg" }}>
                            {item.slotsLeft}
                        </Text>
                    </Text>
                )}
                <div className="m-card-foot-spacer" />
                {renderAction()}
            </div>

            {showReason && (
                <Text
                    sx={{
                        display: "block",
                        fontSize: 0,
                        color: "danger.fg",
                        px: "14px",
                        pb: "10px",
                    }}
                >
                    <i className="fas fa-circle-info" />{" "}
                    {registerBlockedTooltip(item, isFull, subLockReason)}
                </Text>
            )}

            {/* Danh sách lớp thực hành con — khi parent mở rộng. */}
            {expanded && hasLth && (
                <div className="m-subwrap">
                    <div className="m-subwrap-title">Lớp thực hành đính kèm</div>
                    {subClasses.map(sub => (
                        <ClassCard
                            key={sub.classId}
                            item={sub}
                            myList={myList}
                            pendingClassIds={pendingClassIds}
                            confirmedClassIds={confirmedClassIds}
                            gateReason={gateReason}
                            showSubject={false}
                            isSub
                            subType={subListType}
                            parentClassId={item.classId}
                            onPickPair={onPickPair}
                        />
                    ))}
                </div>
            )}
        </MCard>
    );
};

export default ClassCard;
